import { useEffect, useRef, useState } from 'react';

/**
 * Intersection Observer hook — adds 'revealed' class when element scrolls into view.
 * @param {Object} options - threshold, rootMargin
 */
export const useScrollReveal = (options = {}) => {
  const ref = useRef(null);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRevealed(true);
          observer.unobserve(el);
        }
      },
      { threshold: options.threshold || 0.15, rootMargin: options.rootMargin || '0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [options.threshold, options.rootMargin]);

  return [ref, isRevealed];
};

/**
 * Component wrapper that reveals children on scroll with staggered delay.
 */
export const RevealOnScroll = ({ children, delay = 0, direction = 'up', className = '' }) => {
  const [ref, isRevealed] = useScrollReveal();

  const dirClass = {
    up: 'reveal-up',
    left: 'reveal-left',
    right: 'reveal-right',
    scale: 'reveal-scale',
  }[direction] || 'reveal-up';

  return (
    <div
      ref={ref}
      className={`scroll-reveal ${dirClass} ${isRevealed ? 'revealed' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};
